import { registerSW } from "virtual:pwa-register";

/**
 * Updates.
 *
 * The service worker was registered with `prompt` and nothing ever prompted, so
 * an installed app kept serving whatever bundle it first cached — for weeks.
 * Every fix shipped in that time was invisible to anyone who had added the app
 * to their home screen, which is a hard thing to diagnose from the outside: the
 * server is right, the code is right, and the phone is running neither.
 *
 * Auto-updating on its own is not the answer either. A scorer mid-over does not
 * want the page swapped underneath them. So: check on load and every ten
 * minutes, and when something is waiting, say so and let them choose the moment.
 */

let apply: (() => Promise<void>) | null = null;
let waiting = false;
let listeners: ((w: boolean) => void)[] = [];

export function onUpdate(fn: (w: boolean) => void): () => void {
  listeners.push(fn);
  fn(waiting);
  return () => { listeners = listeners.filter((f) => f !== fn); };
}

function announce(): void {
  for (const fn of listeners) fn(waiting);
}


/** Reload onto the new version. Called from a button, never automatically. */
export async function update(): Promise<void> {
  if (apply) await apply();
  else location.reload();
}

export function startUpdates(): void {
  if (typeof window === "undefined") return;
  try {
    apply = registerSW({
      immediate: true,
      onNeedRefresh() { waiting = true; announce(); },
      onRegisteredSW(_url, reg) {
        if (!reg) return;
        // Ten minutes. Frequent enough that a fix lands the same session,
        // infrequent enough to be invisible.
        setInterval(() => { void reg.update(); }, 10 * 60 * 1000);
        // And whenever the app comes back to the foreground, since that is when
        // someone is most likely to be about to use it.
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") void reg.update();
        });
      },
    });
  } catch {
    // No service worker (a plain browser tab, or the native shell). Nothing to
    // update: the APK ships its own assets.
  }
}

/**
 * Which build this is.
 *
 * Stamped at build time so a screenshot is enough to tell whether a device is
 * running the fix being discussed. Guessing at that has cost more than one
 * round of debugging.
 */
export const BUILD = __BUILD_ID__;
